import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Badge,
  Stack,
  useMediaQuery
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard, 
  People, 
  ExitToApp,
  FilterList,
  Person,
  Refresh,
  DateRange,
  Search,
  SettingsOutlined,
  MenuOpen,
  GetApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function AdminPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobile || isTablet);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [activeMenu, setActiveMenu] = useState('dashboard');

  // Update sidebar collapsed state when screen size changes
  useEffect(() => {
    setSidebarCollapsed(isMobile || isTablet);
  }, [isMobile, isTablet]);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // API base URL for production or development
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://chatbot-backend-21oh.onrender.com' 
        : 'http://localhost:5000';
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      // Use auth token with API request
      const leadsResponse = await fetch(`${API_BASE_URL}/api/admin/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!leadsResponse.ok) {
        const errorText = await leadsResponse.text();
        
        // If unauthorized, redirect to login
        if (leadsResponse.status === 401) {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('token');
          navigate('/admin/login');
          throw new Error('Your session has expired. Please login again.');
        }
        
        throw new Error(`Leads API error: ${leadsResponse.status} - ${errorText}`);
      }
      
      const leadsData = await leadsResponse.json();
      console.log('Leads data received:', leadsData);
      
      // Ensure leads data is an array
      const leadsArray = Array.isArray(leadsData) ? leadsData : [];
      setLeads(leadsArray);
      setFilteredLeads(leadsArray);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to fetch data: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters when search or date filters change
  useEffect(() => {
    let result = [...leads];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead => 
        (lead.name && lead.name.toLowerCase().includes(query)) ||
        (lead.email && lead.email.toLowerCase().includes(query)) ||
        (lead.phone && lead.phone.includes(query)) ||
        (lead.organization && lead.organization.toLowerCase().includes(query))
      );
    }
    
    // Apply date filter
    if (dateFilter.startDate) {
      const startDate = new Date(dateFilter.startDate);
      result = result.filter(lead => new Date(lead.timestamp) >= startDate);
    }
    
    if (dateFilter.endDate) {
      const endDate = new Date(dateFilter.endDate);
      // Set end date to the end of the day
      endDate.setHours(23, 59, 59, 999);
      result = result.filter(lead => new Date(lead.timestamp) <= endDate);
    }
    
    setFilteredLeads(result);
  }, [leads, searchQuery, dateFilter]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setDateFilter({
      startDate: '',
      endDate: ''
    });
    setFilteredLeads(leads);
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    // In a real app, you would navigate to different sections
    // For this demo, we'll just change the active menu
  };

  // Sidebar width based on collapsed state
  const sidebarWidth = sidebarCollapsed ? 80 : 260;

  // Function to export leads to CSV
  const exportToCSV = () => {
    if (filteredLeads.length === 0) {
      return;
    }
    
    // Define CSV headers
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Organization',
      'Department',
      'City',
      'User Type',
      'Date'
    ];
    
    // Convert leads data to CSV rows
    const csvRows = filteredLeads.map(lead => [
      lead.name || '',
      lead.email || '',
      lead.phone || '',
      lead.organization || '',
      lead.department || '',
      lead.city || '',
      lead.userType || '',
      new Date(lead.timestamp).toLocaleString() || ''
    ]);
    
    // Add headers to the beginning
    csvRows.unshift(headers);
    
    // Convert each row to CSV format and join with newlines
    const csvContent = csvRows.map(row => 
      row.map(cell => 
        // Escape commas, quotes, etc.
        typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(',')
    ).join('\n');
    
    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create URL for the Blob
    const url = URL.createObjectURL(blob);
    
    // Create a link element to trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    
    // Add to document, click to download, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add date formatting helper function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const year = date.getFullYear();
    return { month, day, year };
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          width: { xs: '100%', sm: `calc(100% - ${sidebarWidth}px)` }, 
          ml: { xs: 0, sm: `${sidebarWidth}px` },
          boxShadow: 'none',
          backgroundColor: '#fff',
          color: '#333',
          borderBottom: '1px solid #e0e0e0',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleSidebar}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleFilter}
              sx={{ mr: 2 }}
            >
              <FilterList />
            </IconButton>
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" color="primary">
                {activeMenu === 'dashboard' && 'Dashboard'}
                {activeMenu === 'leads' && 'Leads Management'}
                {activeMenu === 'analytics' && 'Analytics'}
                {activeMenu === 'settings' && 'Settings'}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {userInfo.firstName ? userInfo.firstName[0] : 'A'}
                </Avatar>
              </IconButton>
            </Stack>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { minWidth: 180 }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Admin User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userInfo.email || 'admin@example.com'}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => handleMenuClick('settings')}>
                <ListItemIcon>
                  <SettingsOutlined fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          position: 'fixed',
          height: '100vh',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          boxShadow: 3,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          zIndex: { xs: 1300, sm: 1200 }, // Higher z-index for mobile
          display: { xs: sidebarCollapsed ? 'none' : 'flex', sm: 'flex' }, // Hide on mobile when collapsed
          flexDirection: 'column'
        }}
      >
        {/* Sidebar Header with Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            padding: sidebarCollapsed ? 2 : '16px 20px',
            height: 64,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {!sidebarCollapsed && (
            <Typography variant="h6" fontWeight="bold" noWrap component="div">
              Admin Panel
            </Typography>
          )}
          <IconButton
            onClick={toggleSidebar}
            sx={{ 
              color: 'white',
              padding: sidebarCollapsed ? 1 : 0,
              display: { xs: 'none', sm: 'block' } // Hide on mobile, we'll use the AppBar button instead
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <MenuOpen />}
          </IconButton>
        </Box>
        
        {/* User Info */}
        {!sidebarCollapsed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 2, color: 'white' }}>
              {userInfo.firstName ? userInfo.firstName[0] : 'A'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Admin User'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {userInfo.email || 'admin@example.com'}
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* Menu Items */}
        <List component="nav" sx={{ pt: 2, flexGrow: 1 }}>
          <ListItem 
            button 
            selected={activeMenu === 'dashboard'} 
            onClick={() => handleMenuClick('dashboard')}
            sx={{ 
              mb: 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              py: 1.5,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              backgroundColor: activeMenu === 'dashboard' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <Dashboard />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Dashboard" />}
          </ListItem>
          
          <ListItem 
            button 
            selected={activeMenu === 'leads'} 
            onClick={() => handleMenuClick('leads')}
            sx={{ 
              mb: 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              py: 1.5,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              backgroundColor: activeMenu === 'leads' ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <People />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Leads" />}
          </ListItem>
        </List>
        
        {/* Bottom Navigation */}
        <Box sx={{ mt: 'auto', mb: 2 }}>
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{ 
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              px: sidebarCollapsed ? 2 : 3,
              borderRadius: sidebarCollapsed ? 0 : '0 25px 25px 0',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              mt: 1,
              py: 1.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'white' }}>
              <ExitToApp />
            </ListItemIcon>
            {!sidebarCollapsed && <ListItemText primary="Logout" />}
          </ListItem>
        </Box>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { 
            xs: '100%', 
            sm: `calc(100% - ${sidebarWidth}px)` 
          },
          ml: { 
            xs: 0, 
            sm: `${sidebarWidth}px` 
          },
          backgroundColor: '#f5f8fb',
          minHeight: '100vh',
          pt: { xs: 8, sm: 9 }
        }}
      >
        {/* Dashboard Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Leads
                </Typography>
                <Typography variant="h3">
                  {leads.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Today's Leads
                </Typography>
                <Typography variant="h3">
                  {leads.filter(lead => {
                    const today = new Date();
                    const leadDate = new Date(lead.timestamp);
                    return leadDate.setHours(0,0,0,0) === today.setHours(0,0,0,0);
                  }).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Filtered Results
                </Typography>
                <Typography variant="h3">
                  {filteredLeads.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Filters Area */}
        {filterOpen && (
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, phone..."
                  InputProps={{
                    startAdornment: <Search color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <DateRange color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <DateRange color="action" sx={{ mr: 1 }} />
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  startIcon={<Refresh />}
                  onClick={resetFilters}
                  variant="outlined"
                  sx={{ height: { xs: '48px', md: '56px' } }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
        
        {/* Main Content */}
        <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#fff' }}>
          <Box 
            sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Typography variant="h6" component="h2">
              Leads Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button 
                variant="outlined" 
                startIcon={<GetApp />}
                onClick={exportToCSV}
                size="small"
                disabled={filteredLeads.length === 0 || loading}
                sx={{ flexGrow: { xs: 1, sm: 0 } }}
              >
                Export CSV
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Refresh />}
                onClick={fetchData}
                size="small"
                sx={{ flexGrow: { xs: 1, sm: 0 }, '&:hover': {
                  backgroundColor: '#9D1680',
                }  }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', my: 4, p: 2 }}>
              <Typography color="error" gutterBottom>{error}</Typography>
              <Button variant="contained" color="primary" onClick={fetchData} sx={{ mt: 2 }}>
                Retry
              </Button>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Organization</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Department</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>City</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>User Type</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeads.length > 0 ? (
                    filteredLeads.map((lead) => {
                      const { month, day, year } = formatDate(lead.timestamp);
                      return (
                        <TableRow key={lead._id} hover>
                          <TableCell>{lead.name}</TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell>{lead.phone}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{lead.organization}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{lead.department}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{lead.city}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{lead.userType}</TableCell>
                          <TableCell>
                            <Card sx={{ 
                              width: 'fit-content', 
                              textAlign: 'center',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              borderRadius: 1,
                              overflow: 'hidden',
                              display: 'inline-flex',
                              flexDirection: 'column',
                              px: '10px',
                              backgroundColor: '#f5f5f5'
                            }}>
                              <Box sx={{ py: 0.2 }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                  {month}
                                </Typography>
                              </Box>
                              <Box sx={{ py: 0.2 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {day}
                                </Typography>
                              </Box>
                              <Box sx={{ py: 0.2 }}>
                                <Typography variant="caption" fontWeight="medium" color="text.secondary">
                                  {year}
                                </Typography>
                              </Box>
                            </Card>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No leads found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AdminPanel; 